import { useEffect, useMemo, useRef, useState } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type { TimelineEventView, TimelineGraphLayout } from "@/features/timeline";
import { cn } from "@/lib/utils";

type TimelineGraphicalViewProps = {
  events: TimelineEventView[];
  layout: TimelineGraphLayout | null;
  onSelectEvent: (eventId: string) => void;
  selectedEventId: string | null;
};

export function TimelineGraphicalView({
  events,
  layout,
  onSelectEvent,
  selectedEventId,
}: TimelineGraphicalViewProps) {
  const { t } = useTranslation();
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [fitToView, setFitToView] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const eventMap = useMemo(
    () => new Map(events.map((event) => [event.id, event])),
    [events],
  );

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(([entry]) => {
      setContainerWidth(entry?.contentRect.width ?? viewport.clientWidth);
    });

    resizeObserver.observe(viewport);
    setContainerWidth(viewport.clientWidth);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (!layout) {
    return null;
  }

  const scale = fitToView && containerWidth > 0 ? Math.min(1, containerWidth / layout.width) : 1;
  const scaledHeight = layout.height * scale;

  return (
    <section className="space-y-6 border-t border-border/70 pt-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-clay">
            {t("timeline.graph.eyebrow")}
          </p>
          <p className="max-w-2xl text-sm leading-7 text-muted-foreground">
            {t("timeline.graph.body", { count: events.length })}
          </p>
        </div>
        <Button
          onClick={() => setFitToView((current) => !current)}
          size="sm"
          type="button"
          variant="outline"
        >
          {fitToView ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          {fitToView
            ? t("timeline.graph.actualSize", { defaultValue: "Actual size" })
            : t("timeline.graph.fitToView", { defaultValue: "Fit timeline" })}
        </Button>
      </div>

      <div
        className={cn("pb-4", fitToView ? "overflow-hidden" : "overflow-x-auto")}
        ref={viewportRef}
      >
        <div
          className={cn("relative", !fitToView && "min-w-[1180px]")}
          style={{
            height: scaledHeight,
            width: fitToView ? "100%" : layout.width,
          }}
        >
          <div
            className="absolute left-0 top-0 origin-top-left"
            style={{
              height: layout.height,
              transform: `scale(${scale})`,
              width: layout.width,
            }}
          >
            <svg
              aria-hidden="true"
              className="absolute inset-0 h-full w-full"
              viewBox={`0 0 ${layout.width} ${layout.height}`}
            >
              <line
                className="text-foreground"
                stroke="currentColor"
                strokeLinecap="round"
                strokeOpacity="0.72"
                strokeWidth="2.5"
                x1={24}
                x2={layout.width - 24}
                y1={layout.axisY}
                y2={layout.axisY}
              />

              {layout.markers.map((marker) => (
                <path
                  d={marker.branchPathD}
                  fill="none"
                  key={marker.eventId}
                  stroke={marker.branchColor}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeOpacity={marker.eventId === selectedEventId ? 1 : 0.82}
                  strokeWidth={marker.eventId === selectedEventId ? 3.5 : 2.5}
                />
              ))}
            </svg>

            {layout.milestones.map((milestone) => (
              <div
                className="absolute -translate-x-1/2"
                key={`${milestone.label}-${milestone.x}`}
                style={{ left: milestone.x, top: 48 }}
              >
                <div className="flex flex-col items-center gap-3">
                  <span className="h-3.5 w-3.5 rounded-full bg-foreground shadow-sm" />
                  <span className="max-w-[11rem] text-center text-sm text-foreground/80">
                    {milestone.label}
                  </span>
                </div>
              </div>
            ))}

            {layout.markers.map((marker) => {
              const event = eventMap.get(marker.eventId);
              const label = marker.linkLabel ?? event?.links[0]?.label ?? null;

              return (
                <button
                  className={cn(
                    "absolute overflow-hidden rounded-[1.35rem] border bg-white/92 px-5 py-4 text-left shadow-[0_24px_60px_rgba(78,55,24,0.08)] backdrop-blur transition-all",
                    marker.eventId === selectedEventId
                      ? "border-foreground/14 ring-1 ring-foreground/8"
                      : "border-white/80 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(78,55,24,0.14)]",
                  )}
                  key={marker.eventId}
                  onClick={() => onSelectEvent(marker.eventId)}
                  style={{
                    height: marker.cardHeight,
                    left: marker.cardX,
                    top: marker.cardY,
                    width: marker.cardWidth,
                  }}
                  type="button"
                >
                  {event?.imageUrl ? (
                    <>
                      <img
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 h-full w-full object-cover opacity-35"
                        src={event.imageUrl}
                      />
                      <span
                        aria-hidden="true"
                        className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.88),rgba(255,255,255,0.97))]"
                      />
                    </>
                  ) : null}

                  <span
                    aria-hidden="true"
                    className="absolute inset-y-0 left-0 w-[3px]"
                    style={{ backgroundColor: marker.branchColor }}
                  />

                  <div className="relative space-y-3">
                    <div className="flex items-start gap-3">
                      <span
                        aria-hidden="true"
                        className="mt-1 h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: marker.branchColor }}
                      />
                      <div className="space-y-1">
                        <h3 className="line-clamp-2 text-[1.05rem] font-semibold leading-6 text-foreground">
                          {marker.title}
                        </h3>
                        <p className="text-xs text-muted-foreground">{marker.eraLabel}</p>
                      </div>
                    </div>

                    <p className="line-clamp-3 text-sm leading-7 text-muted-foreground">
                      {marker.summaryLine || event?.descriptionPreview || event?.description}
                    </p>

                    {label ? (
                      <span className="inline-flex rounded-full bg-secondary/75 px-3 py-1 text-xs text-muted-foreground">
                        {label}
                      </span>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
